import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import api from "../../services/api";

function fmt(n) { return (n||0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const PIE_COLORS = ["#1a9e72","#185fa5","#7c3aed","#d97706","#e24b4a","#6b7280","#10b981","#f59e0b"];

const STATUS_LABELS = {
  trial: "Essai", actif: "Actif", suspendu: "Suspendu", résilié: "Résilié", impayé: "Impayé",
};

export default function MetricsTab({ currentCompanyId }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.abonnements.metrics(currentCompanyId)
      .then(setMetrics)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>Chargement…</div>;
  if (!metrics) return null;

  const byPlanData = Object.entries(metrics.byPlan || {}).map(([name, value], i) => ({ name, value: Math.round(value * 100) / 100 }));
  const byStatusData = Object.entries(metrics.byStatus || {}).map(([name, value]) => ({ name: STATUS_LABELS[name] || name, value }));

  return (
    <div>
      {/* KPIs */}
      <div className="kpi-grid k4" style={{ marginBottom: 24 }}>
        <div className="kpi">
          <div className="kpi-label">💶 MRR</div>
          <div className="kpi-value" style={{ color: "var(--g)" }}>{fmt(metrics.mrr)} €</div>
          <div className="kpi-sub">Revenu mensuel récurrent</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">📈 ARR</div>
          <div className="kpi-value">{fmt(metrics.arr)} €</div>
          <div className="kpi-sub">Revenu annuel récurrent</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">👥 ARPU</div>
          <div className="kpi-value">{fmt(metrics.arpu)} €</div>
          <div className="kpi-sub">Revenu moyen / abonné</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">📉 Churn rate</div>
          <div className="kpi-value" style={{ color: metrics.churnRate > 5 ? "#e24b4a" : metrics.churnRate > 0 ? "#d97706" : "var(--g)" }}>
            {metrics.churnRate}%
          </div>
          <div className="kpi-sub" style={{ color: metrics.churnRate > 5 ? "#e24b4a" : "var(--text-muted)" }}>
            {metrics.churnRate > 5 ? "⚠️ Taux élevé" : "Ce mois"}
          </div>
        </div>
      </div>

      <div className="kpi-grid k3" style={{ marginBottom: 24 }}>
        <div className="kpi"><div className="kpi-label">✅ Abonnés actifs</div><div className="kpi-value">{metrics.totalActive}</div></div>
        <div className="kpi"><div className="kpi-label">🆕 Nouveaux ce mois</div><div className="kpi-value" style={{ color: "#1a9e72" }}>+{metrics.newThisMonth}</div></div>
        <div className="kpi"><div className="kpi-label">❌ Résiliations ce mois</div><div className="kpi-value" style={{ color: metrics.cancelledThisMonth > 0 ? "#e24b4a" : "inherit" }}>-{metrics.cancelledThisMonth}</div></div>
      </div>

      {metrics.churnRate > 5 && (
        <div style={{ padding: "12px 16px", background: "#e24b4a15", border: "1px solid #e24b4a30", borderRadius: 8, marginBottom: 20, fontSize: 13, color: "#e24b4a" }}>
          ⚠️ <strong>Taux de churn élevé ({metrics.churnRate}%)</strong> — Vérifiez les causes de résiliation et relancez les clients impayés.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
        {/* MRR evolution */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Évolution MRR — 12 derniers mois</div>
          {metrics.mrrHistory && metrics.mrrHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={metrics.mrrHistory} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                <defs>
                  <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1a9e72" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#1a9e72" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}€`} />
                <Tooltip formatter={v => [`${fmt(v)} €`, "MRR"]} />
                <Area type="monotone" dataKey="mrr" stroke="#1a9e72" strokeWidth={2} fill="url(#mrrGrad)" dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)", fontSize: 13 }}>Données insuffisantes</div>
          )}
        </div>

        {/* Revenu par plan */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>MRR par plan</div>
          {byPlanData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={byPlanData} cx="50%" cy="50%" outerRadius={70} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {byPlanData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => `${fmt(v)} €`} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ marginTop: 8 }}>
                {byPlanData.map((d, i) => (
                  <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginBottom: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{d.name}</span>
                    <strong>{fmt(d.value)} €</strong>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)", fontSize: 13 }}>Aucun abonné actif</div>
          )}
        </div>
      </div>

      {/* Statuts */}
      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Répartition par statut</div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {byStatusData.map(d => (
            <div key={d.name} style={{ flex: 1, minWidth: 100, textAlign: "center", padding: "12px 16px", background: "#f9fafb", borderRadius: 8 }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{d.value}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{d.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
